import { TFunction } from 'i18next';
import { ElementDownloadLink, ElementType } from 'components/element-viewer/index';
import useBundleTranslation from 'i18n';
import { DownloadFormatType, DownloadLinkElement } from 'components/common/DownloadLink';
import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import DropdownIcon from 'components/common/dropdown-icon/DropdownIcon';
import React, { MouseEvent, useRef } from 'react';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { useIsAdminOrHasPrivilege } from 'hooks/useUserPrivilege';
import { useExternalReportNativeFilters } from 'components/element-viewer/ControlsPanel';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';

interface LinkProps {
    link: ElementDownloadLink;
    elementId: number;
    segmentValueId: number;
    uco?: number;
    filters?: string;
    reference?: string;
    useNativeFilters?: boolean;
    childRef?: React.Ref<HTMLElement>;
}

function downloadFormatToLink(format: DownloadFormatType, t: TFunction) {
    const map = {
        xlsx: { label: 'Excel', icon: 'file-excel' },
        pptx: { label: 'PowerPoint', icon: 'file-powerpoint' },
        pdf: { label: 'PDF', icon: 'file-pdf' },
        csv: { label: t('download.label_csv_raw'), icon: 'file' },
        csvf: { label: t('download.label_csv_formatted'), icon: 'file' },
    };

    return { ...map[format], key: format, url: '' };

    // $tip   = $downloadDataFormat == 'csvf' ? 'ext:qtip="'.sprintf($translator->tt('label_applies_display_mask_formatting_to_data', 'Applies Display Mask %sformatting to data.'), '<br/>').'"' : '';
}

export default function DownloadIcon({
    element,
    segmentValueId,
    uco,
    filters,
    reference,
    useNativeFilters,
}: {
    element: ElementType;
    segmentValueId: number;
    uco?: number;
    filters?: string;
    reference?: string;
    useNativeFilters?: boolean;
    ref?: React.Ref<HTMLElement>;
}) {
    const { t } = useBundleTranslation(['components/common/element', 'components/common/viewer_controls_panel']);
    const downloadDisabled = useConfigConstantValue('DISABLE_DOWNLOADS');
    const allowedDownload = useIsAdminOrHasPrivilege('PRIV_ALLOW_DOWNLOAD');
    const hasIcon = useEmbeddingContextVisible('control_buttons.download');

    if (!hasIcon || (downloadDisabled && !allowedDownload)) {
        return null;
    }

    if (element.customFieldsRestrictions.downloadPrint) {
        const tooltipTitle = (
            <span
                dangerouslySetInnerHTML={{
                    __html: t('cf_restriction', {
                        type: t('download.download'),
                        name: element.customFieldsRestrictions.downloadPrint.name,
                        values: element.customFieldsRestrictions.downloadPrint.values.join(', '),
                    }),
                }}
            />
        );
        return (
            <Tooltip title={tooltipTitle}>
                <span>
                    <IconButton disabled sx={{ p: '6px' }}>
                        <IconMi icon="download" fontSize="16" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    const links = element.download.links.concat(
        element.download?.formats?.map((format: DownloadFormatType) => downloadFormatToLink(format, t)) ?? [],
    );

    if (!links.length) {
        //t('label_no_downloads_available')
        return (
            <Tooltip title={t('download_empty_btn_tooltip')}>
                <span>
                    <IconButton disabled sx={{ p: '6px' }}>
                        <IconMi icon="download" fontSize="16" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    return (
        <span data-test={'top-download-icon'}>
            <DropdownIcon mountWhenClosed={false} icon="download" tooltip={t('download_btn_tooltip')}>
                {links.map((link: ElementDownloadLink) => (
                    <MenuItemWrapper
                        key={link.key}
                        link={link}
                        segmentValueId={segmentValueId}
                        elementId={element.row.elementId}
                        filters={filters}
                        reference={reference}
                        useNativeFilters={useNativeFilters}
                        uco={uco}
                    />
                ))}
            </DropdownIcon>
        </span>
    );
}

function MenuDownloadLink({
    link,
    elementId,
    segmentValueId,
    uco,
    filters,
    reference,
    useNativeFilters,
    childRef,
}: LinkProps) {
    const filtersToGet = useExternalReportNativeFilters(filters, useNativeFilters);
    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            {link.key == 'external' ? (
                <Box
                    ref={childRef}
                    component={'a'}
                    href={link.url}
                    sx={{
                        color: 'inherit',
                        '&:hover': { textDecoration: 'none' },
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <IconMi icon={link.icon} fontSize="16" sx={{ mr: 1 }} /> {link.label}
                </Box>
            ) : (
                <DownloadLinkElement
                    childRef={childRef}
                    elementId={elementId}
                    segmentValueId={segmentValueId}
                    format={link.key}
                    uco={uco}
                    filters={filtersToGet}
                    reference={reference}
                    useNativeFilters={useNativeFilters}
                >
                    <IconMi icon={link.icon} fontSize="16" sx={{ mr: 1 }} />
                    {link.label}
                </DownloadLinkElement>
            )}
        </Box>
    );
}

function MenuItemWrapper(props: LinkProps) {
    const childRef = useRef<HTMLElement>(null);
    const handleWrapperClick = (event: MouseEvent) => {
        if (childRef.current?.click && event.currentTarget == event.target) {
            childRef.current.click();
        }
    };

    return (
        <MenuItem onClick={handleWrapperClick}>
            <MenuDownloadLink {...props} childRef={childRef} />
        </MenuItem>
    );
}
